// Gate lab — gate definitions side by side, as a pivot dimension.
//
// The Eligibility dial replays one candidate at fleet level. This screen
// replays MANY at once, at any grain: the scenario is the FIRST breakdown
// (always — pooling across scenarios would count the same rupee once per
// scenario, so the server forces it and this client shows it pinned), and
// day/screen/fleet/month nest under it. Everything is counterfactual over
// the frozen cab-day numbers; the REAL payout gate changes only on
// Agreements & gates, month-boundary forward.
//
// Same architecture as Spread.js: URL is the state, the server does every
// sum, per-scenario totals come from an independent ungrouped query and are
// checked against the pivot's own scenario rows on every request, and
// conservation identities (pass + gated == total, day and cab partitions)
// run over every returned row.

import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import {
  RefreshCw, Download, SlidersHorizontal, Search, X, Info, ChevronDown, ChevronRight, Plus,
} from 'lucide-react';
import { Loading, ErrorState, Pill } from '../../components/ui';
import useApi from '../../lib/useApi';
import api from '../../../services/api';
import Filters from './Filters';
import DateRange, { toISO, addDays, startOfDay } from './DateRange';
import CustomizePanel from './CustomizePanel';
import s from './Pivot.module.css';

const SUB = 'Gate definitions compared side by side over the same frozen history — the scenario is a '
  + 'pivot dimension, so any combination of minimum trips, driving hours and passenger hours can be '
  + 'sliced by day, screen, fleet or month. Pure replay: nothing here changes the real gate, which '
  + 'lives on the premise-operator agreement and moves only at a month boundary.';

const DIM_LABEL = {
  gate: 'Gate', media_tenant: 'Media owner', screen: 'Screen', day: 'Day', week: 'Week', month: 'Month',
};
const DIM_HELP = {
  gate: 'The scenario under test. Always the first breakdown: every row is a within-scenario statement, never a pool across gates.',
  media_tenant: 'The screen’s current owner — which gate is fair to which fleet.',
  screen: 'Per screen: which cabs a gate hits hardest.',
  day: 'IST day: how each gate would have behaved, day by day.',
  week: 'IST week (Monday start).',
  month: 'IST calendar month.',
};
const MEA_LABEL = {
  spend: 'Earned ₹', spend_pass: '₹ passes', spend_gated: '₹ gated', money_pass_pct: 'Money pass',
  days: 'Cab-days', days_pass: 'Days pass', days_gated: 'Days gated', day_pass_pct: 'Day pass',
  cabs: 'Cabs', cabs_never_gated: 'Cabs never gated', cabs_hit: 'Cabs hit',
};
const MEA_HELP = {
  spend: 'Rupees earned in this slice (same basis as Fleet spread and Screen earnings).',
  spend_pass: 'Rupees on cab-days that CLEAR this scenario — what the fleet would be paid on.',
  spend_gated: 'Rupees on cab-days that FAIL it — earned for the platform, withheld from the fleet under this scenario.',
  money_pass_pct: 'Share of this slice’s rupees on passing days. The one number that compares gates.',
  days: 'Cab-days in the slice.',
  days_pass: 'Cab-days clearing the scenario.',
  days_gated: 'Cab-days failing it.',
  day_pass_pct: 'Share of cab-days clearing the scenario — money pass minus this gap says whether the gate bites idle days (good) or earning days (costly).',
  cabs: 'Distinct screens in the slice.',
  cabs_never_gated: 'Cabs this scenario never gates — their every day clears.',
  cabs_hit: 'Cabs with at least one gated day under this scenario.',
};
const DIM_SECTIONS = [
  { id: 'level', label: 'Level', keys: ['media_tenant', 'screen'] },
  { id: 'time', label: 'Time', keys: ['day', 'week', 'month'] },
];
const MEA_SECTIONS = [
  { id: 'money', label: 'Money', keys: ['spend', 'spend_pass', 'spend_gated', 'money_pass_pct'] },
  { id: 'daysf', label: 'Days', keys: ['days', 'days_pass', 'days_gated', 'day_pass_pct'] },
  { id: 'cabsf', label: 'Cabs', keys: ['cabs', 'cabs_never_gated', 'cabs_hit'] },
];
const VIEWS = [
  { id: 'pivot', label: 'Pivot', help: 'Scenario rows with breakdowns nested under them; each scenario row is verified against an independent ungrouped query.' },
  { id: 'flat', label: 'Ungrouped', help: 'One row per scenario × breakdown combination, no subtotals.' },
];

const MONEY_KEYS = new Set(['spend', 'spend_pass', 'spend_gated']);
const PCT_KEYS = new Set(['money_pass_pct', 'day_pass_pct']);

const n = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN'));
const inr = (v, dp = 2) => (v == null ? '—'
  : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`);
const fmtCell = (key, v) => {
  if (v == null) return '—';
  if (MONEY_KEYS.has(key)) return inr(v);
  if (PCT_KEYS.has(key)) return `${v}%`;
  return n(v);
};
const fmtDay = (iso) => {
  const [y, m, d] = String(iso || '').split('-').map(Number);
  return y && m && d
    ? new Date(y, m - 1, d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';
};
const fmtClock = (ts) => (ts
  ? new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
  : 'never');
const fmtKey = (dim, v) => {
  if (v == null) return null;
  if (dim === 'day' || dim === 'week' || dim === 'month') return String(v).slice(0, 10);
  return String(v);
};

const KEY_W = 200;
const frozenStyle = (i) => ({ left: i * KEY_W, minWidth: KEY_W, maxWidth: KEY_W, width: KEY_W });

function InfoDot({ title }) {
  return (
    <span className={s.dot} tabIndex={0} role="img" aria-label={title} title={title}>
      <Info size={11} aria-hidden="true" />
    </span>
  );
}

function buildTree(rows, dims) {
  const pathAt = (r, d) => dims.slice(0, d).map((k) => `${k}=${r.keys[k] ?? ' '}`).join('/');
  const byPath = new Map();
  const roots = [];
  for (const r of rows) {
    const depth = r.depth == null ? dims.length : r.depth;
    const path = pathAt(r, depth);
    const node = { ...r, depth, path, leaf: depth >= dims.length, children: [] };
    byPath.set(path, node);
    const parent = depth > 1 ? byPath.get(pathAt(r, depth - 1)) : null;
    (parent ? parent.children : roots).push(node);
  }
  return roots;
}
function flattenTree(nodes, collapsed, out = []) {
  for (const node of nodes) {
    out.push(node);
    if (node.children.length && !collapsed.has(node.path)) flattenTree(node.children, collapsed, out);
  }
  return out;
}

// ── the scenario manager: chips in the URL, a three-field add row ───────────
function ScenarioBar({ gates, onChange }) {
  const [t, setT] = useState('4');
  const [d, setD] = useState('');
  const [p, setP] = useState('');
  const add = () => {
    const code = `${t.trim() !== '' ? `t${t.trim()}` : ''}${d.trim() !== '' ? `d${d.trim()}` : ''}${p.trim() !== '' ? `p${p.trim()}` : ''}`;
    if (!code) return;
    if (!gates.some((g) => g.code === code)) onChange([...gates.map((g) => g.code), code]);
  };
  const inputStyle = {
    width: 64, font: 'inherit', fontSize: 12.5, color: 'var(--ink)', background: 'var(--surface)',
    border: '1px solid var(--line)', borderRadius: 7, padding: '4px 7px',
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '0 0 10px' }}>
      <span style={{ fontFamily: 'var(--f-mono)', fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink-mute)' }}
            title="The scenarios under test. Each becomes a row family in the table; remove with ×. The current REAL gate is on Agreements & gates.">
        Scenarios
      </span>
      {gates.map((g) => (
        <span key={g.code} className={s.btn} style={{ cursor: 'default' }} title={`code: ${g.code}`}>
          {g.label}
          {gates.length > 1 && (
            <button type="button" aria-label={`Remove ${g.label}`}
                    onClick={() => onChange(gates.filter((x) => x.code !== g.code).map((x) => x.code))}
                    style={{ border: 0, background: 'none', cursor: 'pointer', color: 'var(--ink-mute)', padding: 0, display: 'grid' }}>
              <X size={11} />
            </button>
          )}
        </span>
      ))}
      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input style={inputStyle} type="number" min="0" value={t} onChange={(e) => setT(e.target.value)} placeholder="trips" aria-label="Min trips" title="Minimum trips per day (blank = no bar)" />
        <input style={inputStyle} type="number" min="0" step="0.5" value={d} onChange={(e) => setD(e.target.value)} placeholder="drive h" aria-label="Min driving hours" title="Minimum driving hours per day (blank = no bar)" />
        <input style={inputStyle} type="number" min="0" step="0.5" value={p} onChange={(e) => setP(e.target.value)} placeholder="pax h" aria-label="Min passenger hours" title="Minimum passenger hours per day (blank = no bar)" />
        <button type="button" className={s.btn} onClick={add} title="Add this combination as a scenario">
          <Plus size={13} aria-hidden="true" /> Add gate
        </button>
      </span>
    </div>
  );
}

export default function Gates() {
  const [params, setParams] = useSearchParams();
  const readList = useCallback(
    (k) => (params.get(k) ? params.get(k).split(',').filter(Boolean) : []), [params]);

  const today = useMemo(() => startOfDay(new Date()), []);
  const range = useMemo(() => ({
    from: params.get('from') || toISO(addDays(today, -29)),
    to: params.get('to') || toISO(today),
  }), [params, today]);

  const gateCodes = useMemo(() => readList('gates'), [readList]);
  const breakdowns = useMemo(() => {
    const b = readList('breakdowns').filter((x) => x !== 'gate');
    return ['gate', ...b];
  }, [readList]);
  const measures = useMemo(() => {
    const m = readList('measures');
    return m.length ? m : ['spend_pass', 'spend_gated', 'money_pass_pct', 'day_pass_pct', 'days_gated', 'cabs_hit'];
  }, [readList]);
  const filters = useMemo(() => ({
    media_tenants: readList('media_tenants'),
    screens: readList('screens'),
  }), [readList]);
  const q = params.get('q') || '';
  const view = params.get('view') === 'flat' ? 'flat' : 'pivot';

  const patch = useCallback((obj) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(obj)) {
      if (Array.isArray(v)) { if (v.length) p.set(k, v.join(',')); else p.delete(k); }
      else if (v) p.set(k, v); else p.delete(k);
    }
    setParams(p, { replace: true });
  }, [params, setParams]);

  const qs = useMemo(() => {
    const p = new URLSearchParams({
      from: range.from, to: range.to, view,
      breakdowns: breakdowns.join(','), measures: measures.join(','),
    });
    if (gateCodes.length) p.set('gates', gateCodes.join(','));
    for (const k of ['media_tenants', 'screens']) {
      if (filters[k].length) p.set(k, filters[k].join(','));
    }
    if (q) p.set('q', q);
    return p.toString();
  }, [range, gateCodes, breakdowns, measures, filters, q, view]);

  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [customizeTab, setCustomizeTab] = useState('breakdowns');
  const [collapsed, setCollapsed] = useState(() => new Set());
  const shape = `${breakdowns.join(',')}|${view}|${gateCodes.join(',')}`;
  useEffect(() => { setCollapsed(new Set()); }, [shape]);

  const [draft, setDraft] = useState(q);
  const lastCommitted = useRef(q);
  useEffect(() => {
    if (q !== lastCommitted.current) { lastCommitted.current = q; setDraft(q); }
  }, [q]);
  useEffect(() => {
    if (draft.trim() === q) return undefined;
    const t = setTimeout(() => { lastCommitted.current = draft.trim(); patch({ q: draft.trim() }); }, 300);
    return () => clearTimeout(t);
  }, [draft, q, patch]);

  const { data, error, loading, reload } = useApi(
    () => api.get(`/insights/gates?${qs}`).then((r) => {
      const d = r.data?.data;
      if (!d || typeof d !== 'object' || !Array.isArray(d.rows)) {
        throw new Error(
          'The server did not answer with the Gate lab payload. If this is a local dev '
          + 'session, the iot-admin server is probably older than the client — restart it '
          + '(Ctrl+C, then ./scripts/dev-iot-admin.sh).');
      }
      return d;
    }), [qs]);

  const first = loading && !data;
  const vocabulary = data?.vocabulary;
  const dimensions = useMemo(
    () => (vocabulary?.dimensions || []).filter((d) => d.key !== 'gate')
      .map((d) => ({ key: d.key, label: DIM_LABEL[d.key] || d.label })),
    [vocabulary],
  );
  const measureDefs = useMemo(
    () => (vocabulary?.measures || []).map((m) => ({ key: m.key, label: MEA_LABEL[m.key] || m.label })),
    [vocabulary],
  );

  const toggle = (list, v, key, max) => {
    const next = list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
    if (max && next.length > max) return;
    patch({ [key]: next.filter((x) => x !== 'gate') });
  };

  const viewBreakdowns = data?.breakdowns?.length ? data.breakdowns : breakdowns;
  const viewMeasures = data?.measures?.length ? data.measures : measures;
  const viewMode = data ? (data.view || 'flat') : view;
  const gates = data?.gates || [];

  const rows = useMemo(() => data?.rows || [], [data]);
  const visible = useMemo(() => {
    if (!rows.length) return [];
    if (viewMode === 'flat') return rows.map((r, i) => ({ ...r, path: `f${i}`, children: [] }));
    return flattenTree(buildTree(rows, viewBreakdowns), collapsed);
  }, [rows, viewBreakdowns, viewMode, collapsed]);

  const toggleNode = (path) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(path)) next.delete(path); else next.add(path);
    return next;
  });

  const exportCsv = () => {
    if (!data?.rows) return;
    const cell = (v) => {
      if (v == null) return '';
      const t = String(v);
      return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
    };
    const cols = [...viewBreakdowns.map((b) => DIM_LABEL[b] || b), ...viewMeasures.map((m) => MEA_LABEL[m] || m)];
    const lines = [cols.map(cell).join(',')];
    for (const r of data.rows) {
      lines.push([
        ...viewBreakdowns.map((b) => (Object.prototype.hasOwnProperty.call(r.keys, b)
          ? (r.labels?.[b] || fmtKey(b, r.keys[b]) || 'unassigned') : '')),
        ...viewMeasures.map((m) => r[m]),
      ].map(cell).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `gate-lab_${range.from}_${range.to}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 0);
  };

  const facets = data?.facets;
  const truncated = data?.truncated;
  const source = data?.source;
  const colCount = viewBreakdowns.length + viewMeasures.length;

  const filterFields = useMemo(() => (facets ? [
    { key: 'media_tenants', label: 'Media owner', chipNoun: 'owner',
      options: (facets.media_tenants || []).map((t) => ({ value: t.id, label: t.name || t.id || 'unassigned' })) },
    { key: 'screens', label: 'Screen', chipNoun: 'screen',
      options: (facets.screens || []).map((sc) => ({ value: sc.id, label: sc.thing_name || sc.id })) },
  ] : null), [facets]);

  return (
    <div className={s.page} data-fill>
      <header className={s.head}>
        <h1 className={s.h1}>Gate lab</h1>
        <InfoDot title={SUB} />

        <div className={s.viewToggle} role="group" aria-label="Table view">
          {VIEWS.map((v) => (
            <button key={v.id} type="button" title={v.help}
                    aria-pressed={viewMode === v.id}
                    className={`${s.viewBtn} ${viewMode === v.id ? s.viewBtnOn : ''}`}
                    onClick={() => patch({ view: v.id === 'pivot' ? '' : v.id })}>
              {v.label}
            </button>
          ))}
        </div>

        <span className={s.spacer} />

        <button type="button" className={s.btn} onClick={reload} disabled={loading} aria-busy={loading}
                title="Re-run the replay over the frozen numbers.">
          <RefreshCw size={13} className={loading ? s.spin : undefined} aria-hidden="true" />
          Refresh
        </button>
        <button type="button" className={s.btn} onClick={exportCsv} disabled={!rows.length}
                title="Download these rows as CSV, exactly as returned.">
          <Download size={13} aria-hidden="true" />
          Export
        </button>
        <button type="button" aria-pressed={customizeOpen}
                className={`${s.btn} ${customizeOpen ? s.btnOn : ''}`}
                onClick={() => setCustomizeOpen((o) => !o)}
                title="Choose the breakdowns nested under each scenario, and the measures.">
          <SlidersHorizontal size={13} aria-hidden="true" />
          Customize
        </button>
      </header>

      <div className={s.controls}>
        <div className={s.searchWrap}>
          <Search size={13} className={s.searchIcon} aria-hidden="true" />
          <input
            type="search" className={s.searchInput} value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Search screen or owner"
            aria-label="Search rows"
            title="Filters on the SERVER — every scenario is re-evaluated over the filtered population."
          />
          {draft && (
            <button type="button" className={s.searchClear} aria-label="Clear search"
                    onClick={() => setDraft('')}>
              <X size={12} />
            </button>
          )}
        </div>
        {facets && filterFields && (
          <Filters compact facets={facets} fields={filterFields} value={filters}
                   onChange={(v) => patch(v)} summary={null} />
        )}
        <span className={s.spacer} />
        <DateRange from={range.from} to={range.to}
                   onChange={(f, t) => patch({ from: f, to: t })} />
      </div>

      <ScenarioBar gates={gates.length ? gates : gateCodes.map((c) => ({ code: c, label: c }))}
                   onChange={(codes) => patch({ gates: codes })} />

      {data?.rollup_agrees === false && (
        <div className={s.parityBad}>
          <Pill tone="bad">SCENARIO TOTALS DISAGREE</Pill>
          <span className={s.parityText}>
            A scenario&rsquo;s pivot row does not match its independent ungrouped total
            ({(data.rollup_mismatched || []).slice(0, 4).join(', ')}). Do not compare gates on this view; report this.
          </span>
        </div>
      )}
      {data?.integrity && data.integrity.violation_count > 0 && (
        <div className={s.parityBad}>
          <Pill tone="bad">IDENTITY VIOLATIONS</Pill>
          <span className={s.parityText}>
            {n(data.integrity.violation_count)} of {n(data.integrity.checks)} conservation identities failed
            (e.g. {data.integrity.violations[0]?.rule}) — a pass/gated split does not add back to its total. Report this.
          </span>
        </div>
      )}
      {truncated && (
        <div className={s.truncated}>
          Showing the first {n(vocabulary?.max_rows)} rows — narrow the window, drop a breakdown or remove a scenario.
        </div>
      )}

      <div className={s.body}>
        <div className={s.main}>
          <div className={`${s.tableWrap} ${loading && data ? s.stale : ''}`}>
            {first && <Loading what="the gate lab" />}
            {!first && error && <ErrorState error={error} onRetry={reload} />}
            {!first && !error && (
              <table className={s.table}>
                <thead>
                  <tr>
                    {viewBreakdowns.map((b, i) => (
                      <th key={b}
                          className={`${s.hKey} ${s.frozen} ${i === viewBreakdowns.length - 1 ? s.frozenEdge : ''}`}
                          style={frozenStyle(i)}>
                        {DIM_LABEL[b] || b}
                        <InfoDot title={DIM_HELP[b]} />
                      </th>
                    ))}
                    {viewMeasures.map((m) => (
                      <th key={m} className={s.hNum}>
                        {MEA_LABEL[m] || m}
                        <InfoDot title={MEA_HELP[m]} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((node) => {
                    const isSub = viewMode === 'pivot' && !node.leaf;
                    const open = !collapsed.has(node.path);
                    return (
                      <tr key={node.path} className={isSub ? s.subRow : undefined}>
                        {viewBreakdowns.map((b, i) => {
                          const frozenCls = `${s.frozen} ${i === viewBreakdowns.length - 1 ? s.frozenEdge : ''}`;
                          const show = viewMode === 'flat' || i === node.depth - 1;
                          if (!show) return <td key={b} className={`${s.key} ${frozenCls}`} style={frozenStyle(i)} />;
                          const label = node.labels?.[b] || fmtKey(b, node.keys[b]);
                          const id = node.keys[b];
                          return (
                            <th key={b} scope="row" className={`${s.key} ${frozenCls}`}
                                style={frozenStyle(i)}
                                title={node.labels?.[b] ? `${node.labels[b]} · ${id}` : undefined}>
                              <span className={s.keyBody}>
                                {viewMode === 'pivot' && node.children.length > 0 ? (
                                  <button type="button" className={s.chev}
                                          aria-expanded={open}
                                          aria-label={`${open ? 'Collapse' : 'Expand'} ${label || 'unassigned'}`}
                                          onClick={() => toggleNode(node.path)}>
                                    {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                  </button>
                                ) : viewMode === 'pivot' && i === 0 && viewBreakdowns.length > 1
                                  ? <span className={s.chevGap} /> : null}
                                {label == null
                                  ? <span className={s.null}>unassigned</span>
                                  : <span className={s.keyName}>{label}</span>}
                                {node.labels?.[b] && id != null && (
                                  <span className={s.keyId}>{String(id).slice(0, 8)}</span>
                                )}
                              </span>
                            </th>
                          );
                        })}
                        {viewMeasures.map((m) => (
                          <td key={m} className={s.num}>{fmtCell(m, node[m])}</td>
                        ))}
                      </tr>
                    );
                  })}
                  {!visible.length && (
                    <tr><td colSpan={colCount} className={s.empty}>
                      Nothing matches this window and these filters.
                    </td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {data && (
            <div className={s.strip}>
              <span title="IST calendar buckets throughout.">
                {fmtDay(range.from)} – {fmtDay(range.to)} · Indian Standard Time
              </span>
              <span>
                {n(gates.length)} scenario{gates.length === 1 ? '' : 's'} · {n(visible.length)} of {n(rows.length)}{truncated ? '+' : ''} rows
              </span>
              {data.integrity && data.integrity.violation_count === 0 && (
                <span className={s.ok}
                      title="Every row of every scenario passed its conservation identities (₹ pass + ₹ gated = ₹ total; day and cab partitions sum exactly), and each scenario's pivot row matches an independent ungrouped total. Re-checked on every request.">
                  {n(data.integrity.checks)} identities checked · 0 violations{data.rollup_agrees ? ' · scenario totals verified' : ''}
                </span>
              )}
              {data.regime && (
                <span title="The rupee atoms are priced by the regime that governed each day: the billing predicate before 1 Aug 2026, presence-verified money from it. Books and recon are never filtered this way — this is a display rule.">
                  {data.regime.note}
                </span>
              )}
              <span className={s.ok} title="This screen only replays. The real gate is a premise-operator agreement term and changes only there, effective at a month boundary.">
                Replay only — commit a winner on <NavLink to="/v2/payouts/agreements" className={s.link}>Agreements &amp; gates</NavLink>
              </span>
              <span className={s.updated}
                    title="When the money mart was last refreshed, not when this page loaded.">
                Updated {fmtClock(source?.refreshed_at)}
              </span>
            </div>
          )}
        </div>

        {customizeOpen && vocabulary && (
          <CustomizePanel
            tab={customizeTab}
            onTab={setCustomizeTab}
            dimensions={dimensions}
            measures={measureDefs}
            dimensionSections={DIM_SECTIONS}
            measureSections={MEA_SECTIONS}
            selectedBreakdowns={breakdowns.filter((b) => b !== 'gate')}
            selectedMeasures={measures}
            onToggleBreakdown={(k) => toggle(breakdowns.filter((b) => b !== 'gate'), k, 'breakdowns', vocabulary.max_breakdowns)}
            onToggleMeasure={(k) => toggle(measures, k, 'measures')}
            onClose={() => setCustomizeOpen(false)}
            maxBreakdowns={vocabulary.max_breakdowns}
            help={{ ...DIM_HELP, ...MEA_HELP }}
            footNote={(
              <>
                The Gate scenario is always the first breakdown — every row is a within-scenario
                statement. Replay only; the real gate changes on Agreements &amp; gates.
              </>
            )}
          />
        )}
      </div>
    </div>
  );
}
